(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root.document) api.mount(root);
})(typeof window === 'undefined' ? globalThis : window, function () {
  const normalize = value => String(value || '').trim().toUpperCase();
  const validPercentage = value => Number.isFinite(Number(value)) && Number(value) > 0 && Number(value) <= 100;

  function discountPrice(cents, offer, giftCard = false) {
    const amount = Number(cents);
    if (giftCard || !offer || !Number.isSafeInteger(amount) || amount <= 0 || !validPercentage(offer.percentage)) return null;
    return Math.max(0, amount - Math.round(amount * Number(offer.percentage) / 100));
  }

  function selectOffer(cart, offers = [], now = Date.now()) {
    if (!cart || !Array.isArray(cart.discount_codes)) return null;
    const codes = cart.discount_codes.map(entry => ({ ...entry, code: normalize(entry.code) }));
    if (Number(cart.item_count) === 0) {
      let selected = null;
      for (const entry of [...codes].reverse()) {
        const offer = offers.find(item => normalize(item.code) === entry.code);
        if (!offer || offer.enabled === false || !validPercentage(offer.percentage)) continue;
        const starts = offer.startsAt ? Date.parse(offer.startsAt) : -Infinity;
        const ends = offer.endsAt ? Date.parse(offer.endsAt) : Infinity;
        if (now >= starts && now < ends && (!selected || Number(offer.percentage) > selected.percentage)) {
          selected = { code: entry.code, percentage: Number(offer.percentage) };
        }
      }
      return selected;
    }
    const accepted = new Set(codes.filter(entry => entry.applicable === true).map(entry => entry.code));
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application)),
    ].filter(Boolean);
    const unique = [...new Map(applications.map(app => [JSON.stringify([app.title, app.type, app.value, app.target_selection]), app])).values()];
    const affectingPrices = unique.filter(app => app.target_type === 'line_item');
    if (affectingPrices.length !== 1) return null;
    const app = affectingPrices[0];
    if (app.type !== 'discount_code' || app.value_type !== 'percentage' || app.target_selection !== 'all'
      || !accepted.has(normalize(app.title)) || !validPercentage(app.value)) return null;
    return { code: normalize(app.title), percentage: Number(app.value) };
  }

  async function resolveOffers(cart, offers, crypto) {
    const resolved = offers.filter(offer => offer.code);
    if (!crypto?.subtle) return resolved;
    for (const entry of cart?.discount_codes || []) {
      const code = normalize(entry.code);
      const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(code));
      const hash = [...new Uint8Array(bytes)].map(value => value.toString(16).padStart(2, '0')).join('');
      const match = offers.find(offer => offer.codeHash === hash);
      if (match) resolved.push({ ...match, code });
    }
    return resolved;
  }

  function mount(win) {
    const doc = win.document;
    const configElement = doc.querySelector('[data-discount-preview-config]');
    if (!configElement) return;
    const config = JSON.parse(configElement.textContent);
    let cart = null;
    let offer = null;
    let pending = null;
    let applying = false;
    let revision = 0;
    const path = name => `${String(win.Monci.root).replace(/\/?$/, '/')}${name}`;
    const money = cents => new Intl.NumberFormat(doc.documentElement.lang || 'hr', {
      style: 'currency', currency: cart?.currency || config.currency || 'EUR',
    }).format(cents / 100);
    const copy = (key, values = {}) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key.toUpperCase()}__`, String(value)), config.strings[key] || '');

    function renderPrice(node) {
      const price = Number(node.dataset.basePrice);
      const discounted = discountPrice(price, offer, node.dataset.giftCard === 'true');
      const active = discounted !== null && discounted < price;
      const regular = node.querySelector('[data-catalog-regular]');
      const promo = node.querySelector('[data-catalog-promo]');
      if (!regular || !promo) return;
      regular.hidden = active;
      promo.hidden = !active;
      node.classList.toggle('has-code-price', active);
      node.closest('[data-product-price-block]')?.classList.toggle('has-code-price', active);
      if (active) {
        promo.querySelector('s').textContent = money(price);
        promo.querySelector('strong').textContent = money(discounted);
        promo.querySelector('small').textContent = copy('with_code', { code: offer.code, percent: offer.percentage });
      }
    }

    function renderControls(node) {
      const applied = node.querySelector('[data-discount-applied]');
      const remove = node.querySelector('[data-discount-remove]');
      const summary = node.querySelector('[data-discount-summary]');
      if (summary) summary.textContent = offer ? copy('selected', { code: offer.code, percent: offer.percentage }) : config.strings.heading;
      if (applied) {
        applied.textContent = offer ? copy('selected', { code: offer.code, percent: offer.percentage }) : '';
        applied.hidden = !offer;
      }
      if (remove) remove.hidden = !(cart?.discount_codes?.length);
    }

    function renderTree(root = doc) {
      const prices = [...root.querySelectorAll('[data-catalog-price]')];
      if (root.matches?.('[data-catalog-price]')) prices.unshift(root);
      prices.forEach(renderPrice);
      root.querySelectorAll('[data-discount-control]').forEach(renderControls);
      if (root.matches?.('[data-discount-control]')) renderControls(root);
    }

    async function accept(nextCart) {
      const currentRevision = revision;
      const resolved = await resolveOffers(nextCart, config.offers, win.crypto);
      if (currentRevision !== revision) return;
      cart = nextCart;
      offer = selectOffer(cart, resolved);
      renderTree();
    }

    async function sync(nextCart) {
      if (nextCart) { revision += 1; await accept(nextCart); return nextCart; }
      if (pending || applying) return pending;
      const requestRevision = revision;
      pending = fetch(path('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
        .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
        .then(async next => { if (requestRevision === revision) await accept(next); return next; })
        .catch(() => { if (requestRevision === revision) { offer = null; renderTree(); } return null; })
        .finally(() => { pending = null; });
      return pending;
    }

    function setStatus(control, text, error = false) {
      if (!control) return;
      const status = control.querySelector('[data-discount-status]');
      if (status) { status.textContent = text; status.classList.toggle('is-error', error); }
    }

    async function refreshViews(next) {
      const page = doc.querySelector('[data-cart-page-section]');
      if (page) {
        const url = new URL(win.Monci.cartUrl, win.location.origin);
        url.searchParams.set('section_id', page.dataset.cartPageSection);
        const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) { win.location.reload(); return; }
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = parsed.querySelector('[data-cart-page-section]');
        if (!replacement) { win.location.reload(); return; }
        page.replaceWith(replacement);
      }
      await win.Monci.refreshCart?.(false, next);
      renderTree();
    }

    async function apply(code, control, remove = false) {
      if (applying) return;
      const normalized = normalize(code);
      if (!remove && (!normalized || normalized.length > 128)) return;
      const waitingForCart = pending;
      applying = true;
      revision += 1;
      control?.querySelectorAll('button, input').forEach(element => { element.disabled = true; });
      setStatus(control, copy('updating'));
      try {
        if (waitingForCart) {
          const before = await waitingForCart;
          if (before) cart = before;
        }
        const codes = [...new Set([...(cart?.discount_codes || []).map(entry => normalize(entry.code)), normalized].filter(Boolean))];
        const response = await fetch(path('cart/update.js'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: remove ? '' : codes.join(',') }),
        });
        if (!response.ok) throw new Error('Discount unavailable');
        const next = await response.json();
        await accept(next);
        const currentUrl = new URL(win.location.href);
        if (currentUrl.searchParams.has('discount')) {
          currentUrl.searchParams.delete('discount');
          win.history.replaceState(win.history.state, '', currentUrl);
        }
        const knownEmptyOffer = Number(next.item_count) === 0 && selectOffer(
          { ...next, discount_codes: [{ code: normalized }] },
          await resolveOffers({ discount_codes: [{ code: normalized }] }, config.offers, win.crypto),
        );
        const accepted = remove || knownEmptyOffer || (offer?.code === normalized)
          || next.discount_codes?.some(entry => normalize(entry.code) === normalized && entry.applicable);
        await refreshViews(next);
        const statusText = accepted ? remove ? copy('removed') : offer && offer.code !== normalized
          ? copy('better_code', { code: offer.code, percent: offer.percentage })
          : copy(Number(next.item_count) ? 'applied' : 'saved')
          : copy(Number(next.item_count) ? 'invalid' : 'unconfirmed');
        doc.querySelectorAll('[data-discount-control]').forEach(node => {
          renderControls(node);
          node.open = true;
          setStatus(node, statusText, !accepted);
          const input = node.querySelector('[data-discount-input]');
          if (input) input.value = accepted ? '' : normalized;
        });
      } catch {
        setStatus(control, copy('error'), true);
      } finally {
        applying = false;
        control?.querySelectorAll('button, input').forEach(element => { element.disabled = false; });
      }
    }

    win.MonciPromo = {
      sync,
      markup(amount, productType) {
        const price = Number(amount);
        if (!Number.isSafeInteger(price) || price < 0) return '';
        const eligible = config.clothingTypes.includes(String(productType || '').toLowerCase());
        return `<span class="monci-price" data-catalog-price data-base-price="${price}" data-gift-card="${!eligible}"><span data-catalog-regular>${money(price)}</span><span class="monci-price__promo" data-catalog-promo hidden><s></s><strong></strong><small></small></span></span>`;
      },
    };
    doc.addEventListener('click', event => {
      const applyButton = event.target.closest('[data-discount-apply]');
      const removeButton = event.target.closest('[data-discount-remove]');
      const button = applyButton || removeButton;
      if (!button) return;
      event.preventDefault();
      const control = button.closest('[data-discount-control]');
      apply(control.querySelector('[data-discount-input]').value, control, !!removeButton);
    });
    doc.addEventListener('keydown', event => {
      if (event.key === 'Enter' && event.target.matches('[data-discount-input]')) {
        event.preventDefault();
        apply(event.target.value, event.target.closest('[data-discount-control]'));
      }
    });
    doc.addEventListener('monci:cart-updated', event => sync(event.detail?.cart));
    doc.addEventListener('monci:price-updated', event => {
      const price = event.target.querySelector('[data-catalog-price]');
      if (price) { price.dataset.basePrice = String(event.detail.price); renderPrice(price); }
    });
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1 && (node.matches('[data-catalog-price], [data-discount-control]')
        || node.querySelector('[data-catalog-price], [data-discount-control]'))) renderTree(node);
    }))).observe(doc.body, { childList: true, subtree: true });
    win.addEventListener('pageshow', event => { if (event.persisted) sync(); });
    doc.addEventListener('visibilitychange', () => { if (doc.visibilityState === 'visible') sync(); });
    const urlCode = new URL(win.location.href).searchParams.get('discount');
    if (urlCode) sync().then(() => apply(urlCode, null)); else sync();
  }

  return { selectOffer, discountPrice, resolveOffers, mount };
});
